/**
 * Root application component. Handles real-time chat via Laravel Echo.
 * 應用程式的根組件，透過 Laravel Echo 處理即時聊天。
 */
import { useEffect, useRef } from 'react';
import { Outlet, ScrollRestoration, matchPath, useLocation, useNavigate, useNavigation } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import ServerErrorPage from '@/pages/ServerErrorPage';
import Loading from '@/components/ui/Loading';
import AccountSuspendedModal from '@/features/auth/components/AccountSuspendedModal';
import IdolSuspendedModal from '@/features/idol/components/IdolSuspendedModal';
import IdolOnlinePoller from '@/components/shared/IdolOnlinePoller';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { getIdolArray, selectIsIdolLocked } from './shared/store/slices/idolSlice';
import { prependNotification, fetchNotifications } from './shared/store/slices/notificationSlice';
import { messageReceived, setTypingIdolId, clearTypingIdolId, fetchRoomList } from './shared/store/slices/chatSlice';
import { bumpReconnectTick } from './shared/store/slices/appSlice';

function App() {
  const userId = useSelector(state => state.user.userId);
  const idolId = useSelector(state => state.user.idolId);
  const token = useSelector(state => state.user.token);
  const isNetworkError = useSelector(state => state.app.isNetworkError);
  // 偶像停權鎖定（見 docs/suspension-plan.md §5.2）：導向我的偶像、暫停以偶像為單位的請求與 WS 訂閱
  const isIdolLocked = useSelector(selectIsIdolLocked);
  const reconnectTick = useSelector(state => state.app.reconnectTick);
  const dispatch = useDispatch();
  const echoRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  // 站內前往 route lazy 的頁面時，chunk 下載完前停在原頁，用 Loading 給回饋（見 main.jsx lazyPage）
  const navigation = useNavigation();
  const chatChannelRef = useRef(null);
  const bellChannelRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // 載入偶像陣列（當有 token 時）
  useEffect(() => {
    if (token) {
      dispatch(getIdolArray());
    }
  }, [token, dispatch]);

  // 頁面切換 / WS 重連時更新聊天室列表和通知列表
  // 注意：故意不將 idolId 加入依賴陣列，因為 idolSwitch 會在 dispatch 後立即 window.location.href
  // 如果 idolId 變更就觸發，會在跳轉前執行 fetchRoomList，造成 redux-persist 儲存不完整的狀態
  // reconnectTick：WS 重連訊號（見 CLAUDE.md「WebSocket 重連與資料補拉」），跳動代表剛重連完成
  useEffect(() => {
    if (token && idolId && !isIdolLocked) {
      dispatch(fetchRoomList());
      dispatch(fetchNotifications({ page: 1, limit: 10 }));
    }
  }, [token, location.pathname, reconnectTick, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // 偶像停權鎖定時一律導向我的偶像，由 IdolSuspendedModal 提供切換／創建偶像與登出
  // 略過 /auth-redirect：後台認證重導會覆蓋登入狀態，不能被舊的鎖定狀態搶先導走
  // 已知限制：若本機持久化的列表過期（後端已解除停權），列表重抓後會解鎖，
  // 但上方的聊天室／通知請求要等下一次換頁才會補抓（刻意不把 isIdolLocked 放進該 effect 的依賴）
  useEffect(() => {
    if (isIdolLocked && location.pathname !== '/idol-list' && !matchPath('/auth-redirect', location.pathname)) {
      navigate('/idol-list', { replace: true });
    }
  }, [isIdolLocked, location.pathname, navigate]);

  // Set robots meta tag based on environment variable
  // 根據環境變數設置 robots meta 標籤
  useEffect(() => {
    const allowIndexing = import.meta.env.VITE_ALLOW_INDEXING === 'true';

    // Remove existing robots meta tag if any
    // 移除現有的 robots meta 標籤
    const existingMeta = document.querySelector('meta[name="robots"]');
    if (existingMeta) {
      existingMeta.remove();
    }

    // Create new robots meta tag
    // 創建新的 robots meta 標籤
    const robotsMeta = document.createElement('meta');
    robotsMeta.name = 'robots';
    robotsMeta.content = allowIndexing ? 'index, follow' : 'noindex, nofollow';
    document.head.appendChild(robotsMeta);

    // Clean up on unmount
    // 組件卸載時清理
    return () => {
      const meta = document.querySelector('meta[name="robots"]');
      if (meta) {
        meta.remove();
      }
    };
  }, []);

  // 注入 Google Analytics (gtag.js)，僅在有設定 VITE_GA_MEASUREMENT_ID 時（正式站 .env.production）
  // SPA 換頁的 page_view 由下方 location effect 手動送，故 config 設 send_page_view: false 避免重複計算
  useEffect(() => {
    const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (!gaId || document.getElementById('ga-gtag')) return;

    const loader = document.createElement('script');
    loader.id = 'ga-gtag';
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(loader);

    // 初始化直接在這裡執行，不用 inline <script>：正式建置的 CSP（vite.config.js）不允許 inline script
    // gtag.js 只認 arguments 物件，不能改成 rest 參數陣列
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', gaId, { send_page_view: false });
  }, []);

  // SPA 換頁時送 page_view（涵蓋首次載入）
  // 略過 /auth-redirect：網址帶有登入 token，送出會把 token 交給 GA
  useEffect(() => {
    if (!import.meta.env.VITE_GA_MEASUREMENT_ID || typeof window.gtag !== 'function') return;
    if (matchPath('/auth-redirect', location.pathname)) return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  // Initialize and clean up Echo instance on mount or token change
  // 初始化並在 token 變化時清除 Echo 實例
  // WS 重連策略見 CLAUDE.md「WebSocket 重連與資料補拉」
  useEffect(() => {
    if (!token) return;

    window.Pusher = Pusher;
    echoRef.current = new Echo({
      broadcaster: 'pusher',
      key: import.meta.env.VITE_REACT_APP_REVERB_APP_KEY,
      wsHost: import.meta.env.VITE_REACT_APP_REVERB_HOST,
      wsPort: 80,
      wssPort: 443,
      forceTLS: true,
      disableStats: true,
      enabledTransports: ['ws', 'wss'],
      cluster: 'us1',
      encrypted: true,
      // 預設 120s/30s 對 Safari 背景斷線偵測過慢，縮短讓 pusher 更快觸發內建重連
      activityTimeout: 30000,
      pongTimeout: 10000,
      authEndpoint: `${import.meta.env.VITE_REACT_APP_API_URL}/broadcasting/auth`,
      auth: {
        headers: { Authorization: `Bearer ${token}` }
      }
    });

    const pusher = echoRef.current.connector.pusher;

    // 只在「真的重連」時廣播 reconnectTick（跳過首次連線，避免與頁面初始載入的 fetch 撞單）
    // 各 feature 自行訂閱 reconnectTick 補拉斷線期間漏掉的資料
    let hasConnectedOnce = false;
    const handleConnected = () => {
      if (!hasConnectedOnce) {
        hasConnectedOnce = true;
        if (import.meta.env.DEV) console.log('[Pusher] initial connected');
        return;
      }
      if (import.meta.env.DEV) console.log('[Pusher] reconnected → bumpReconnectTick');
      dispatch(bumpReconnectTick());
    };
    pusher.connection.bind('connected', handleConnected);

    // DEV-only：觀察連線狀態變化（unavailable / failed / connecting / connected …）
    const handleStateChange = import.meta.env.DEV
      ? (states) => console.log('[Pusher] state_change', states.previous, '→', states.current)
      : null;
    const handleError = import.meta.env.DEV
      ? (err) => console.log('[Pusher] error', err)
      : null;
    if (handleStateChange) pusher.connection.bind('state_change', handleStateChange);
    if (handleError) pusher.connection.bind('error', handleError);

    // Safari 等瀏覽器會在背景 tab 暫停 WS（"closed due to suspension"），
    // tab 回到 visible 時主動觸發重連。實際補拉邏輯由各 feature 訂閱 reconnectTick 處理。
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const state = pusher.connection.state;
      if (state !== 'connected' && state !== 'connecting') {
        if (import.meta.env.DEV) console.log('[Pusher] visibilitychange → reconnect, current state:', state);
        pusher.connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 組件卸載時清理 Echo 實例
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      pusher.connection.unbind('connected', handleConnected);
      if (handleStateChange) pusher.connection.unbind('state_change', handleStateChange);
      if (handleError) pusher.connection.unbind('error', handleError);
      if (echoRef.current) {
        echoRef.current.disconnect();
        echoRef.current = null;
      }
    };
  }, [token, dispatch]);

  // 使用 ref 保存最新的 idolId，讓 WS 通知 handler 能讀到當前選中偶像而不需重新訂閱
  const idolIdRef = useRef(idolId);
  useEffect(() => {
    idolIdRef.current = idolId;
  }, [idolId]);

  // 統一監聽聊天訊息（只在這裡訂閱，避免重複 auth）
  // 依賴 location.pathname 而非 idolId，避免 idolSwitch 在跳轉前觸發訂閱
  useEffect(() => {
    if (!echoRef.current || !userId || !idolId || isIdolLocked) return;

    // 清理舊的訂閱
    if (chatChannelRef.current) {
      chatChannelRef.current.stopListening('MessageSent');
      chatChannelRef.current.stopListening('TypingSent');
    }

    // 訂閱聊天頻道
    const chatChannel = echoRef.current.private(`chat.${userId}.${idolId}`);
    chatChannelRef.current = chatChannel;

    // 交給 chatSlice 依當下的聊天室列表與開著的聊天室處理（當前聊天對象的訊息轉給 ChatPage，其餘累加未讀）
    chatChannel.listen('MessageSent', (event) => {
      dispatch(messageReceived(event.message));
    });

    // 監聽對方正在輸入
    chatChannel.listen('TypingSent', (event) => {
      const senderIdolId = event.data?.sender_idol_id;
      if (senderIdolId) {
        dispatch(setTypingIdolId(senderIdolId));

        // 清除之前的超時
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        // 5 秒後自動清除 typing 狀態（略長於發送間隔 3 秒，避免閃爍）
        typingTimeoutRef.current = setTimeout(() => {
          dispatch(clearTypingIdolId());
        }, 5000);
      }
    });

    return () => {
      if (chatChannelRef.current) {
        chatChannelRef.current.stopListening('MessageSent');
        chatChannelRef.current.stopListening('TypingSent');
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [userId, location.pathname, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // 偶像停權鎖定時離開目前偶像的聊天頻道（上方訂閱 effect 只在 pathname 改變時重跑，
  // 且 cleanup 只 stopListening、不 leave）。解鎖不處理：切換偶像是整頁重載，創建偶像會換頁觸發重新訂閱
  useEffect(() => {
    if (!isIdolLocked || !chatChannelRef.current) return;
    chatChannelRef.current.stopListening('MessageSent');
    chatChannelRef.current.stopListening('TypingSent');
    echoRef.current.leave(`chat.${userId}.${idolId}`);
    chatChannelRef.current = null;
  }, [isIdolLocked, userId, idolId]);

  // 監聽鈴鐺通知（獨立訂閱，因為是不同頻道）
  useEffect(() => {
    if (!echoRef.current || !userId) return;

    // 清理舊的訂閱
    if (bellChannelRef.current) {
      bellChannelRef.current.stopListening('BellNotificationSent');
    }

    const bellChannel = echoRef.current.private(`bell.${userId}`);
    bellChannelRef.current = bellChannel;

    bellChannel.listen('BellNotificationSent', (event) => {
      if (!event.notification) return;
      // 只接收目前選中偶像的通知（payload 內含 idol_id），其餘偶像的通知丟棄
      if (Number(event.notification.idol_id) !== Number(idolIdRef.current)) return;
      dispatch(prependNotification(event.notification));
    });

    return () => {
      if (bellChannelRef.current) {
        bellChannelRef.current.stopListening('BellNotificationSent');
      }
    };
  }, [userId, dispatch]);

  if (isNetworkError) {
    return <ServerErrorPage />;
  }

  return (
    <>
      <Outlet />
      {navigation.state === 'loading' && <Loading />}
      <ScrollRestoration />
      <AccountSuspendedModal />
      <IdolSuspendedModal />
      <IdolOnlinePoller />
    </>
  );
}

export default App;
